import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Search, Mail, MessageSquare, Check, Archive, Eye, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'read', label: 'Read', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'replied', label: 'Replied', color: 'bg-green-500/20 text-green-400' },
  { value: 'archived', label: 'Archived', color: 'bg-stone-500/20 text-stone-400' }
];

const AdminContacts = () => {
  const { token } = useAuth();
  const { refreshStats } = useOutletContext();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/contacts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      toast.error('Error loading contacts');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (contactId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/contacts/${contactId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Status updated!');
        fetchContacts();
        refreshStats();
        if (selectedContact?.id === contactId) {
          setSelectedContact({ ...selectedContact, status: newStatus });
        }
      }
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    return option?.color || 'bg-stone-500/20 text-stone-400';
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || contact.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const newCount = contacts.filter(c => c.status === 'new').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-terracota border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-contacts">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-serif text-white mb-1">Contact Messages</h1>
          <p className="text-stone-400 text-sm">
            {contacts.length} total messages
            {newCount > 0 && <span className="text-terracota ml-2">({newCount} new)</span>}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-stone-800 border border-stone-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-terracota"
        >
          <option value="">All Status</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Contacts List */}
      <div className="space-y-3">
        {filteredContacts.length === 0 ? (
          <div className="bg-stone-800 rounded-xl p-8 text-center border border-stone-700">
            <p className="text-stone-400">No messages found</p>
          </div>
        ) : (
          filteredContacts.map((contact) => (
            <div 
              key={contact.id}
              className={`bg-stone-800 rounded-xl border ${contact.status === 'new' ? 'border-terracota/50' : 'border-stone-700'} p-4`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-white font-medium">{contact.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(contact.status)}`}>
                      {contact.status}
                    </span>
                  </div>
                  <p className="text-stone-400 text-sm mb-1">{contact.email}</p>
                  {contact.company && (
                    <p className="text-stone-500 text-sm mb-2">{contact.company}</p>
                  )}
                  <p className="text-stone-300 text-sm line-clamp-2">{contact.description}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-xs bg-stone-700 text-stone-300 px-2 py-1 rounded">
                      {contact.project_type}
                    </span>
                    <span className="text-xs text-stone-500">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedContact(contact)}
                    className="p-2 text-stone-400 hover:text-white transition-colors"
                    title="View details"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  {contact.status === 'new' && (
                    <button
                      onClick={() => updateStatus(contact.id, 'read')}
                      className="p-2 text-stone-400 hover:text-blue-400 transition-colors"
                      title="Mark as read"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                  )}
                  <a
                    href={`mailto:${contact.email}?subject=Re: ${contact.project_type}`}
                    className="p-2 text-stone-400 hover:text-terracota transition-colors"
                    title="Reply via email"
                    onClick={() => updateStatus(contact.id, 'replied')}
                  >
                    <Mail className="w-5 h-5" />
                  </a>
                  <button
                    onClick={() => updateStatus(contact.id, 'archived')}
                    className="p-2 text-stone-400 hover:text-stone-200 transition-colors"
                    title="Archive"
                  >
                    <Archive className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Contact Details Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-stone-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-stone-700">
            <div className="flex items-center justify-between p-4 border-b border-stone-700">
              <h2 className="text-lg font-semibold text-white">Message Details</h2>
              <button 
                onClick={() => setSelectedContact(null)}
                className="text-stone-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto max-h-[70vh]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl text-white font-medium">{selectedContact.name}</h3>
                  <p className="text-stone-400">{selectedContact.email}</p>
                  {selectedContact.company && (
                    <p className="text-stone-500 text-sm">{selectedContact.company}</p>
                  )}
                </div>
                <select
                  value={selectedContact.status}
                  onChange={(e) => updateStatus(selectedContact.id, e.target.value)}
                  className="bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-terracota"
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-stone-700/50 rounded-lg p-3">
                  <p className="text-stone-400 text-xs mb-1">Project Type</p>
                  <p className="text-white">{selectedContact.project_type}</p>
                </div>
                {selectedContact.timeline && (
                  <div className="bg-stone-700/50 rounded-lg p-3">
                    <p className="text-stone-400 text-xs mb-1">Timeline</p>
                    <p className="text-white">{selectedContact.timeline}</p>
                  </div>
                )}
                {selectedContact.budget && (
                  <div className="bg-stone-700/50 rounded-lg p-3">
                    <p className="text-stone-400 text-xs mb-1">Budget</p>
                    <p className="text-white">{selectedContact.budget}</p>
                  </div>
                )}
                <div className="bg-stone-700/50 rounded-lg p-3">
                  <p className="text-stone-400 text-xs mb-1">Received</p>
                  <p className="text-white">{new Date(selectedContact.created_at).toLocaleString()}</p>
                </div>
              </div>

              <div>
                <p className="text-stone-400 text-sm mb-2">Message</p>
                <div className="bg-stone-700/50 rounded-lg p-4">
                  <p className="text-white whitespace-pre-wrap">{selectedContact.description}</p>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-stone-700">
                <a
                  href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.project_type}`}
                  onClick={() => updateStatus(selectedContact.id, 'replied')}
                  className="flex-1 flex items-center justify-center gap-2 bg-terracota text-white py-3 rounded-lg hover:bg-terracota/90 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  Reply via Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
